import React, { useCallback, useEffect, useState } from "react";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RouteProp } from "@react-navigation/native";
import { StyleSheet, View } from "react-native";
import * as Sentry from "@sentry/react-native";
import LTILayout from "../layouts/LTILayout";
import InteractiveLearningLTIEditFormContainer from "../containers/customCoursework/InteractiveLearningForm/InteractiveLearningLTIEditFormContainer";
import type { RootStackParamList } from "../navigation/navigation.types";
import { useAppConfig } from "../AppConfigProvider";
import { useAuth } from "../hooks/useAuth";
import Loader from "../components/common/status/Loader/Loader";
import LTIErrorView from "./LTIErrorView";

function LTIEditILEView() {
  const route = useRoute<RouteProp<RootStackParamList, "LTI Edit ILE">>();
  const { activityId, accessToken: routeAccessToken, refreshToken: routeRefreshToken } = route.params ?? {};
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { login, isAuthenticated, didCheckToken } = useAuth();
  const appConfig = useAppConfig();
  const currentDomain = appConfig?.currentDomain;
  const imageGenerationSupportFlag = appConfig?.imageGenerationSupportFlag;
  const [authReady, setAuthReady] = useState(false);
  const [submitError, setSubmitError] = useState<{ code: string; message: string } | null>(null);

  useFocusEffect(
    useCallback(() => {
      if (!routeAccessToken || !routeRefreshToken) return;

      let isActive = true;

      const bootstrapAuth = async () => {
        try {
          await login(routeAccessToken, routeRefreshToken);
        } catch (error) {
          Sentry.captureException(error);
          if (!isActive) return;
          navigation.reset({
            index: 0,
            routes: [{ name: "LTIErrorView", params: { error: "auth_failed" } }],
          });
          return;
        }
        if (!isActive) return;
        setAuthReady(true);
      };

      void bootstrapAuth();

      return () => {
        isActive = false;
      };
    }, [login, navigation, routeAccessToken, routeRefreshToken])
  );

  useEffect(() => {
    if (routeAccessToken && routeRefreshToken) return;
    if (!didCheckToken) return;
    if (isAuthenticated) {
      setAuthReady(true);
    } else {
      navigation.reset({
        index: 0,
        routes: [{ name: "LTIErrorView", params: { error: "auth_failed" } }],
      });
    }
  }, [didCheckToken, isAuthenticated, navigation, routeAccessToken, routeRefreshToken]);

  const handleSuccess = (ileId: string, _title: string) => {
    navigation.navigate("LTI Analytics", { activityId: ileId });
  };

  const handleError = (error: Error) => {
    Sentry.captureException(error);
    setSubmitError({ code: "unknown", message: error.message || "Failed to update interactive learning experience" });
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  if (submitError) {
    return <LTIErrorView error={submitError.code} message={submitError.message} />;
  }

  if (!authReady) {
    return (
      <LTILayout>
        <View style={styles.loaderWrap}>
          <Loader />
        </View>
      </LTILayout>
    );
  }

  if (!activityId) {
    return <LTIErrorView error="invalid_launch" message="Missing activity ID." />;
  }

  return (
    <LTILayout>
      <View style={styles.container}>
        <InteractiveLearningLTIEditFormContainer
          activityId={activityId}
          onSuccess={handleSuccess}
          onError={handleError}
          onCancel={handleCancel}
          currentDomain={currentDomain}
          imageGenerationSupportFlag={imageGenerationSupportFlag}
        />
      </View>
    </LTILayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  loaderWrap: {
    marginTop: 24,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default LTIEditILEView;
